<template>
  <div
    class="thumbnail"
    :class="[
      props.size ? `thumbnail--${props.size}` : '',
      props.fitContent ? 'px-1' : '',
    ]"
  >
    <img
      v-if="props.image"
      :src="src"
      :alt="props.name"
      :loading="lazyImage ? 'lazy' : undefined"
    >
    <span
      v-else-if="initials"
      :class="size === 'xs' ? 'text-xs font-semibold' : ''"
    >
      {{ initials }}
    </span>
  </div>
</template>

<script setup>
const props = defineProps(buildProps({
  size: {
    type: String,
    values: ['xs', 'sm', 'md', 'lg', 'xl', '2xl', '3xl', '4xl', '5xl'],
    default: 'md',
  },
  image: {
    type: String,
    default: null,
  },
  fitContent: {
    type: Boolean,
    default: false,
  },
  name: {
    type: String,
    default: '',
  },
  lazyImage: {
    type: Boolean,
    default: true,
  },
}));

// Computed
const transformations = computed(() => {
  const transformations = [
    'tr:w-420',
    'h-240',
  ];

  if (props.fitContent) {
    transformations.push('cm-pad_resize');
  }

  return transformations.join(',');
});

// Computed
const initials = computed(() => {
  if (!(props.name && props.name.trim().length)) {
    return false;
  }

  return props.name[0];
});

const src = computed(() => {
  if ((props.image || '').startsWith('http') && useRuntimeConfig().public.imagekit) {
    return `${useRuntimeConfig().public.imagekit}/${transformations.value}/${props.image}`;
  }

  return props.image;
});
</script>

<style>
.thumbnail {
  @apply border border-gray-200 overflow-hidden flex-shrink-0 flex items-center justify-center;
  @apply text-xl font-semibold text-gray-50;

  &--xs {
    @apply rounded;
    width: 2.625rem;
    height: 1.5rem;
  }

  &--sm {
    @apply rounded;
    width: 3.5rem;
    height: 2rem;
  }

  &--md {
    @apply rounded;
    width: 4.375rem;
    height: 2.5rem;
  }

  &--lg {
    @apply rounded-md;
    width: 5.25rem;
    height: 3rem;
  }

  &--xl {
    @apply rounded-md;
    width: 6rem;
    height: 3.5rem;
  }

  &--2xl {
    @apply rounded-lg;
    width: 7rem;
    height: 4rem;
  }

  &--3xl {
    @apply rounded-lg;
    width: 9.625rem;
    height: 5.5rem;
  }

  &--4xl {
    @apply rounded-lg;
    width: 10.5rem;
    height: 6rem;
  }

  &--5xl {
    @apply rounded-lg;
    width: 13.125rem;
    height: 7.5rem;
  }
}
</style>
